import { useEffect, useState } from 'react';
import { getStorageModuleState } from '@/shared/app-features-api';
import { subscribeStorageModuleOff } from '@/shared/storage-module';
import type { StorageModuleState } from '@/contracts/app-features';

/**
 * NEX-1645 (plan 2026-10-20, Tarea 3.4) — the module's state (`GET /app/features`) for Login,
 * Registro, the shell and Solicitudes: `null` while it loads, then `off`, `registration_only` or
 * `full`.
 *
 * [ASSUMED] A failed read (network, 5xx, a body off the contract) or one slower than
 * `READ_TIMEOUT_MS` is `full`: the panel belongs to the depósito, and the API blocks anyway what the
 * state does not allow (503 `STORAGE_MODULE_DISABLED` / `STORAGE_REQUESTS_DISABLED`, whose copy
 * `describeStorageError` shows). The client web reads a failure the other way (`off`), because
 * there it decides what a client is offered.
 *
 * One read per page load, shared by every caller: a successful answer is kept for the rest of the
 * session (a later mount reads it synchronously, so the shell's pages never flash the wrong
 * state); a failure is not kept, so the next mount asks again. A flag turned to `off` in the
 * backoffice reaches an open panel through the API's 503 `STORAGE_MODULE_DISABLED`: the transports
 * report it (`storage-module.ts`, audit CTR-3) and the kept state becomes `off` right away. Any
 * other change (`off`/`registration_only` → `full`) still waits for the next load.
 */
const READ_TIMEOUT_MS = 5000;

let known: StorageModuleState | null = null;
let pending: Promise<StorageModuleState> | null = null;

function loadStorageModuleState(): Promise<StorageModuleState> {
  if (known) return Promise.resolve(known);
  if (!pending) {
    pending = new Promise<StorageModuleState>((resolve) => {
      const timer = setTimeout(() => resolve('full'), READ_TIMEOUT_MS);
      getStorageModuleState()
        .then(
          (state) => {
            known = state;
            resolve(state);
          },
          () => resolve('full')
        )
        .finally(() => {
          clearTimeout(timer);
          pending = null;
        });
    });
  }
  return pending;
}

export function useStorageModuleState(): StorageModuleState | null {
  const [state, setState] = useState<StorageModuleState | null>(known);

  useEffect(() => {
    let cancelled = false;
    void loadStorageModuleState().then((next) => {
      if (!cancelled) setState(next);
    });
    const unsubscribe = subscribeStorageModuleOff(() => {
      known = 'off';
      if (!cancelled) setState('off');
    });
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  return state;
}
